import { ExtractTimePipe } from './../../../shared/pipes/extract-time.pipe';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoadBalanceService } from '../load-balance-service/load-balance.service';
import {
  LoadBalanceRecordInterface,
  LoadBalanceRecordRow,
} from 'src/app/shared/models/request-loadBalance-record.model';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable, Subject, takeUntil, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import {
  LOAD_BALANCE_GROUPS,
  LOAD_BALANCE_LINES,
  LoadBalanceLineGroup,
} from './load-balance-lines';

type SaveState = 'saved' | 'error';

const VACATION_KEY = 'vacation';
/** Per quanto resta il segno "salvato" accanto al valore appena modificato. */
const SAVED_FEEDBACK_MS = 2000;

@Component({
  selector: 'app-load-balance-dashboard',
  templateUrl: './load-balance-dashboard.component.html',
  styleUrls: ['./load-balance-dashboard.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    ExtractTimePipe,
  ],
})
export class LoadBalanceDashboardComponent implements OnInit, OnDestroy {
  loadBalanceRecords: LoadBalanceRecordRow[] = [];
  operatorsForm: FormArray;
  isLoading = true;
  loadFailed = false;

  readonly groups = LOAD_BALANCE_GROUPS;
  /** Macroarea mostrata: 'all' o l'id di un gruppo. */
  selectedGroup = 'all';

  /** Esito dell'ultimo salvataggio per cella, chiave `${indiceOperatore}:${businessLine | 'vacation'}`. */
  private saveStates: Record<string, SaveState> = {};
  private unsubscribe = new Subject<void>();

  constructor(
    private loadBalanceService: LoadBalanceService,
    private fb: FormBuilder,
    private toast: ToastService
  ) {
    this.operatorsForm = this.fb.array([]);
  }

  ngOnInit(): void {
    this.getUsers();
  }

  getUsers() {
    this.isLoading = true;
    this.loadFailed = false;
    this.loadBalanceService
      .getLoadBalance()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (loadbalance) => {
          this.loadBalanceRecords = loadbalance.content.map((item) => ({
            ...item,
            selected: false,
          }));
          this.initForm();
          this.isLoading = false;
        },
        error: () => {
          this.loadFailed = true;
          this.isLoading = false;
        },
      });
  }

  initForm() {
    this.operatorsForm.clear();
    this.loadBalanceRecords.forEach((record) => {
      record.isExpired = record.factorExpiry
        ? this.expiringDateCHeck(record.factorExpiry)
        : false;

      const weights = record.businessLineWeights as unknown as Record<string, number>;
      const weightControls = LOAD_BALANCE_LINES.reduce((controls, line) => {
        controls[line.control] = new FormControl(weights[line.businessLine]);
        return controls;
      }, {} as Record<string, FormControl>);

      const group = this.fb.group({
        onVacation: new FormControl(record.onVacation),
        ...weightControls,
        boost: new FormControl(record.factorExpiry),
      });
      this.operatorsForm.push(group);
    });
  }

  getOperatorFormGroup(index: number): FormGroup {
    return this.operatorsForm.at(index) as FormGroup;
  }

  /** Gruppi mostrati come colonne: tutti, oppure solo quello scelto. */
  get visibleGroups(): LoadBalanceLineGroup[] {
    return this.selectedGroup === 'all'
      ? this.groups
      : this.groups.filter((g) => g.id === this.selectedGroup);
  }

  /** Numero di colonne della tabella, per la riga "nessun operatore". */
  get columnCount(): number {
    return 2 + this.visibleGroups.reduce((n, g) => n + g.lines.length, 0);
  }

  selectGroup(id: string): void {
    this.selectedGroup = id;
  }

  /** Classe Bootstrap della cella: verde appena salvata, rossa se il salvataggio è fallito. */
  cellClass(index: number, key: string): Record<string, boolean> {
    const state = this.saveStates[`${index}:${key}`];
    return { 'is-valid': state === 'saved', 'is-invalid': state === 'error' };
  }

  updateOperatorVacation(index: number): void {
    const record = this.loadBalanceRecords[index];
    const formGroup = this.operatorsForm.at(index) as FormGroup;

    const updatedData = {
      ...record,
      onVacation: formGroup.get('onVacation')?.value,
    };
    this.save(
      `${index}:${VACATION_KEY}`,
      this.loadBalanceService.updateVacation(updatedData)
    );
  }

  //UPDATE BUSINESSLINE WEIGHT
  updateOperatorWeight(index: number, businessLine: string): void {
    const record = this.loadBalanceRecords[index];
    const formGroup = this.operatorsForm.at(index) as FormGroup;
    const key = `${index}:${businessLine}`;

    const businessLineWeights = LOAD_BALANCE_LINES.reduce((weights, line) => {
      weights[line.businessLine] = formGroup.get(line.control)?.value;
      return weights;
    }, {} as Record<string, number>);

    // Il peso è tra 0 e 1: un valore vuoto o fuori scala non viene mandato al backend.
    const changed = businessLineWeights[businessLine];
    if (typeof changed !== 'number' || isNaN(changed) || changed < 0 || changed > 1) {
      this.saveStates[key] = 'error';
      this.toast.setAction('Il peso deve essere un numero tra 0 e 1', false);
      return;
    }

    const updatedData = {
      ...record,
      businessLineWeights:
        businessLineWeights as unknown as LoadBalanceRecordInterface['businessLineWeights'],
    };
    this.save(key, this.loadBalanceService.updateWeight(updatedData, businessLine));
  }

  private save(key: string, request$: Observable<unknown>): void {
    request$.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saveStates[key] = 'saved';
        timer(SAVED_FEEDBACK_MS)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(() => {
            if (this.saveStates[key] === 'saved') {
              delete this.saveStates[key];
            }
          });
      },
      error: () => {
        this.saveStates[key] = 'error';
        this.toast.setAction('Errore durante il salvataggio, riprova', false);
      },
    });
  }

  expiringDateCHeck(factorExpiry: Date): boolean {
    const expiryDate = new Date(factorExpiry);
    const now = new Date();
    const offset = expiryDate.getTimezoneOffset() * -60000; // Offset in millisecondi
    const adjustedExpiryDate = new Date(expiryDate.getTime() + offset);
    return adjustedExpiryDate.getTime() > now.getTime();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
